
import React, { useState } from 'react';
import { ComputeParams } from '../types';
import { vehicleService, driverService, warehouseService } from '../api';
import { WarehouseLocation } from '../api/types';
import { useLoading } from '../context/LoadingContext';
import LocationPicker from './LocationPicker';

interface TruckFormProps {
  params: ComputeParams;
  onChange: (updates: Partial<ComputeParams>) => void;
  onCompute: () => void;
  isLoading: boolean;
  onToast?: (message: string) => void;
  onShowMap?: () => void;
}

const TruckForm: React.FC<TruckFormProps> = ({ params, onChange, onCompute, isLoading, onToast, onShowMap }) => {
  const { withLoading } = useLoading();
  const [loadingFreeVehicles, setLoadingFreeVehicles] = useState(false);
  const [loadingFreeDrivers, setLoadingFreeDrivers] = useState(false);
  const [availableTrucks, setAvailableTrucks] = useState<any[]>([]);
  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [pickerTarget, setPickerTarget] = useState<'driver' | 'truck' | null>(null);
  const [warehouses, setWarehouses] = useState<Record<string, WarehouseLocation>>({});

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!params.truckId || params.truckId === '') errors.truckId = 'Please select a truck';
    if (!params.driverId || params.driverId === '') errors.driverId = 'Please select a driver';
    if (!params.date || params.date === '') errors.date = 'Please select a date';
    if (!params.time || params.time === '') errors.time = 'Please select a time';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleComputeWithValidation = () => {
    if (validateForm()) {
      onCompute();
    } else {
      onToast?.('✗ Please fill all required fields');
    }
  };

  const handleFreeVehicles = async () => {
    setLoadingFreeVehicles(true);
    try {
      const result = await withLoading(() => vehicleService.freeVehicles());
      if (result.status === 200) {
        onToast?.('✓ All vehicles freed successfully');
        window.location.reload();
      } else {
        onToast?.('✗ Failed to free vehicles');
      }
    } catch {
      onToast?.('✗ Error: Could not free vehicles');
    } finally {
      setLoadingFreeVehicles(false);
    }
  };

  const handleFreeDrivers = async () => {
    setLoadingFreeDrivers(true);
    try {
      const result = await withLoading(() => driverService.freeDrivers());
      if (result.status === 200) {
        onToast?.('✓ All drivers freed successfully');
        window.location.reload();
      } else {
        onToast?.('✗ Failed to free drivers');
      }
    } catch {
      onToast?.('✗ Error: Could not free drivers');
    } finally {
      setLoadingFreeDrivers(false);
    }
  };

  const handleLocationConfirm = (lat: number, lng: number) => {
    if (pickerTarget === 'driver') {
      onChange({ driverLat: lat, driverLong: lng });
      onToast?.(`✓ Driver location set: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    } else if (pickerTarget === 'truck') {
      onChange({ vehicleLat: lat, vehicleLong: lng });
      onToast?.(`✓ Truck location set: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    }
    setPickerTarget(null);
  };

  const fetchAvailableTrucks = async () => {
    const resp = await withLoading(() => vehicleService.getFreeVehicles());
    if (resp.status === 200) {
      setAvailableTrucks(resp?.vehicles?.map((v: string) => ({ id: v, name: v })) || []);
    } else {
      onToast?.('✗ Failed to fetch available trucks');
    }
  };

  const fetchAvailableDrivers = async () => {
    const resp = await withLoading(() => driverService.getFreeDrivers());
    if (resp.status === 200) {
      const drivers = Object.entries(resp?.data?.data || {}).map(([id, name]) => ({ id, name }));
      setAvailableDrivers(drivers);
    } else {
      onToast?.('✗ Failed to fetch available drivers');
    }
  };

  React.useEffect(() => {
    fetchAvailableTrucks();
    fetchAvailableDrivers();
    // Fetch warehouse locations for the map
    warehouseService.getWarehouseLocations().then(resp => {
      if (resp.status === 200 && resp.data) {
        setWarehouses(resp.data);
      }
    }).catch(() => { });
  }, []);

  // Helper to show current location status
  const driverLocationSet = params.driverLat !== 0 && params.driverLong !== 0;
  const truckLocationSet = params.vehicleLat !== 0 && params.vehicleLong !== 0;

  return (
    <>
      {/* Location Picker Dialog */}
      <LocationPicker
        isOpen={pickerTarget !== null}
        title={pickerTarget === 'driver' ? 'Mark Driver Location' : 'Mark Truck Location'}
        onConfirm={handleLocationConfirm}
        onClose={() => setPickerTarget(null)}
        initialLat={pickerTarget === 'driver' ? params.driverLat : params.vehicleLat}
        initialLng={pickerTarget === 'driver' ? params.driverLong : params.vehicleLong}
        warehouses={warehouses}
      />

      <div className="rounded-xl shadow-sm p-6 flex flex-col gap-5 w-full md:w-80 shrink-0" style={{ backgroundColor: 'var(--ih-theme-bg-card)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
        {/* Input Parameters */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-heading)' }}>Input Parameters</h2>
          <div className="space-y-4">
            {/* Select Truck */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ih-theme-text-tertiary)' }}>Select Truck</label>
              <select
                value={params.truckId}
                onChange={(e) => {
                  onChange({ truckId: e.target.value });
                  setValidationErrors(prev => ({ ...prev, truckId: '' }));
                }}
                className="w-full rounded-lg text-sm p-2.5 transition-colors"
                style={{
                  backgroundColor: 'var(--ih-theme-bg-elevated)',
                  borderWidth: '1px',
                  borderColor: validationErrors.truckId ? 'var(--ih-theme-error)' : 'var(--ih-theme-border-light)',
                  color: 'var(--ih-theme-text-primary)',
                  fontFamily: 'var(--ih-font-family-primary)',
                }}
              >
                <option value="" disabled>-- Select a Truck --</option>
                {availableTrucks?.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {validationErrors.truckId && (
                <p className="text-xs mt-1" style={{ color: 'var(--ih-theme-error)' }}>{validationErrors.truckId}</p>
              )}
            </div>

            {/* Select Driver */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ih-theme-text-tertiary)' }}>Select Driver</label>
              <select
                value={params.driverId}
                onChange={(e) => {
                  onChange({ driverId: e.target.value });
                  setValidationErrors(prev => ({ ...prev, driverId: '' }));
                }}
                className="w-full rounded-lg text-sm p-2.5 transition-colors"
                style={{
                  backgroundColor: 'var(--ih-theme-bg-elevated)',
                  borderWidth: '1px',
                  borderColor: validationErrors.driverId ? 'var(--ih-theme-error)' : 'var(--ih-theme-border-light)',
                  color: 'var(--ih-theme-text-primary)',
                  fontFamily: 'var(--ih-font-family-primary)',
                }}
              >
                <option value="" disabled>-- Select a Driver --</option>
                {availableDrivers?.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {validationErrors.driverId && (
                <p className="text-xs mt-1" style={{ color: 'var(--ih-theme-error)' }}>{validationErrors.driverId}</p>
              )}
            </div>

            {/* Mark Driver Location */}
            <button
              onClick={() => setPickerTarget('driver')}
              className="w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 shadow-sm active:scale-95"
              style={{ backgroundColor: 'var(--ih-theme-accent)', color: 'var(--ih-theme-white)' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--ih-theme-accent-dark)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--ih-theme-accent)')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              Mark driver location
            </button>
            {driverLocationSet && (
              <p className="text-xs -mt-2 ml-1" style={{ color: 'var(--ih-theme-success)' }}>📍 {params.driverLat.toFixed(4)}, {params.driverLong.toFixed(4)}</p>
            )}

            {/* Mark Truck Location */}
            <button
              onClick={() => setPickerTarget('truck')}
              className="w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 shadow-sm active:scale-95"
              style={{ backgroundColor: 'var(--ih-theme-bg-elevated)', color: 'var(--ih-theme-text-primary)', borderWidth: '1px', borderColor: 'var(--ih-theme-border-medium)' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--ih-theme-bg-tertiary)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--ih-theme-bg-elevated)')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10" /></svg>
              Mark Truck location
            </button>
            {truckLocationSet && (
              <p className="text-xs -mt-2 ml-1" style={{ color: 'var(--ih-theme-success)' }}>📍 {params.vehicleLat.toFixed(4)}, {params.vehicleLong.toFixed(4)}</p>
            )}

            {/* Date */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ih-theme-text-tertiary)' }}>Available Date</label>
              <input
                type="date"
                value={params.date}
                onChange={(e) => {
                  onChange({ date: e.target.value });
                  setValidationErrors(prev => ({ ...prev, date: '' }));
                }}
                className="w-full rounded-lg text-sm p-2.5 transition-colors"
                style={{
                  backgroundColor: 'var(--ih-theme-bg-elevated)',
                  borderWidth: '1px',
                  borderColor: validationErrors.date ? 'var(--ih-theme-error)' : 'var(--ih-theme-border-light)',
                  color: 'var(--ih-theme-text-primary)',
                  colorScheme: 'dark',
                }}
              />
              {validationErrors.date && (
                <p className="text-xs mt-1" style={{ color: 'var(--ih-theme-error)' }}>{validationErrors.date}</p>
              )}
            </div>

            {/* Time */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ih-theme-text-tertiary)' }}>Available Time</label>
              <input
                type="time"
                value={params.time}
                onChange={(e) => {
                  onChange({ time: e.target.value });
                  setValidationErrors(prev => ({ ...prev, time: '' }));
                }}
                className="w-full rounded-lg text-sm p-2.5 transition-colors"
                style={{
                  backgroundColor: 'var(--ih-theme-bg-elevated)',
                  borderWidth: '1px',
                  borderColor: validationErrors.time ? 'var(--ih-theme-error)' : 'var(--ih-theme-border-light)',
                  color: 'var(--ih-theme-text-primary)',
                  colorScheme: 'dark',
                }}
              />
              {validationErrors.time && (
                <p className="text-xs mt-1" style={{ color: 'var(--ih-theme-error)' }}>{validationErrors.time}</p>
              )}
            </div>
          </div>
        </div>

        {/* Compute Button */}
        <button
          onClick={handleComputeWithValidation}
          disabled={isLoading}
          className="w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2"
          style={{
            backgroundColor: isLoading ? 'var(--ih-theme-bg-elevated)' : 'var(--ih-theme-accent)',
            color: isLoading ? 'var(--ih-theme-text-muted)' : 'var(--ih-theme-white)',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            boxShadow: isLoading ? 'none' : '0 4px 14px rgba(59, 130, 246, 0.3)',
          }}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" style={{ color: 'var(--ih-theme-text-muted)' }} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Computing...
            </>
          ) : 'Compute Routes'}
        </button>

        {/* Fleet Utilities */}
        <div className="pt-4" style={{ borderTopWidth: '1px', borderColor: 'var(--ih-theme-border-light)' }}>
          <h2 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--ih-theme-text-primary)', fontFamily: 'var(--ih-font-family-heading)' }}>Fleet Utilities</h2>
          <div className="space-y-2">
            <button
              onClick={handleFreeDrivers}
              disabled={loadingFreeDrivers || isLoading}
              className="w-full text-left px-4 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center justify-between"
              style={{
                backgroundColor: (loadingFreeDrivers || isLoading) ? 'var(--ih-theme-bg-secondary)' : 'var(--ih-theme-bg-elevated)',
                color: (loadingFreeDrivers || isLoading) ? 'var(--ih-theme-text-muted)' : 'var(--ih-theme-text-secondary)',
                cursor: (loadingFreeDrivers || isLoading) ? 'not-allowed' : 'pointer',
                borderWidth: '1px',
                borderColor: 'var(--ih-theme-border-light)',
              }}
            >
              <span className="flex items-center gap-2">
                {loadingFreeDrivers && (
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                )}
                Free All Drivers
              </span>
              <svg className="w-4 h-4" style={{ color: 'var(--ih-theme-text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7" /></svg>
            </button>

            <button
              onClick={handleFreeVehicles}
              disabled={loadingFreeVehicles || isLoading}
              className="w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: (loadingFreeVehicles || isLoading) ? 'var(--ih-theme-bg-secondary)' : 'rgba(59, 130, 246, 0.1)',
                color: (loadingFreeVehicles || isLoading) ? 'var(--ih-theme-text-muted)' : 'var(--ih-theme-accent-light)',
                cursor: (loadingFreeVehicles || isLoading) ? 'not-allowed' : 'pointer',
                borderWidth: '2px',
                borderColor: (loadingFreeVehicles || isLoading) ? 'var(--ih-theme-border-light)' : 'var(--ih-theme-accent)',
              }}
            >
              {loadingFreeVehicles && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              )}
              Free all Trucks
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TruckForm;
